/**
 * MSI — banner de calificación + productos sugeridos en el cart drawer.
 *
 * Dos productos dinámicos: complementarios (tipo/tags/color), mismo género,
 * misma talla en stock, elegidos al azar de la colección.
 */
(function () {
  'use strict';

  const SELECTOR = '[data-msi-milestones]';
  const IGNORED_TAGS = new Set([
    'sale',
    'new',
    'nuevo',
    'nueva',
    'bestseller',
    'msi',
    'featured',
    'destacado',
    'mujer',
    'hombre'
  ]);

  let collectionCache = null;
  const productByHandleCache = {};

  function formatCurrency(amount) {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return '$' + Math.round(amount).toLocaleString('es-MX');
    }
  }

  function parseAmount(value, fallback) {
    const n = Number(String(value == null ? '' : value).replace(/[^\d.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  function getConfig() {
    const cfg = (window.theme && window.theme.msiMilestones) || {};
    const milestonesSrc = Array.isArray(cfg.milestones) && cfg.milestones.length
      ? cfg.milestones
      : [
          { amount: 2000, reward: '3 MSI' },
          { amount: 6000, reward: '12 MSI' }
        ];

    return {
      milestones: milestonesSrc
        .map((m) => ({
          amount: parseAmount(m.amount, 0),
          reward: m.reward || 'MSI'
        }))
        .filter((m) => m.amount > 0)
        .sort((a, b) => a.amount - b.amount),
      collectionHandle: cfg.collectionHandle || '',
      maxPrice: parseAmount(cfg.maxPrice, 0)
    };
  }

  function getRoots() {
    return Array.from(document.querySelectorAll(SELECTOR));
  }

  function formatRewardPhrase(reward) {
    const raw = String(reward || '').trim();
    if (!raw) return 'meses sin intereses';
    if (/meses/i.test(raw)) return raw;

    const match = raw.match(/(\d+)\s*msi/i);
    if (match) return match[1] + ' meses sin intereses';

    return raw;
  }

  function getHighestUnlocked(subtotal, milestones) {
    let unlocked = null;
    milestones.forEach((m) => {
      if (subtotal >= m.amount) unlocked = m;
    });
    return unlocked;
  }

  async function fetchCollectionProducts(handle) {
    if (!handle) return [];
    if (collectionCache && collectionCache.handle === handle) return collectionCache.products;

    try {
      const res = await fetch('/collections/' + handle + '/products.json?limit=250');
      if (!res.ok) return [];
      const data = await res.json();
      const products = (data.products || []).filter((p) => p.available !== false);
      collectionCache = { handle: handle, products: products };
      return products;
    } catch (err) {
      console.warn('[msi-milestones] No se pudieron cargar productos', err);
      return [];
    }
  }

  async function fetchProductByHandle(handle) {
    if (!handle) return null;
    if (productByHandleCache[handle]) return productByHandleCache[handle];

    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      const product = await res.json();
      productByHandleCache[handle] = product;
      return product;
    } catch (err) {
      console.warn('[msi-milestones] No se pudo cargar producto', handle, err);
      return null;
    }
  }

  function pickVariant(product) {
    if (!product.variants || !product.variants.length) return null;
    return product.variants.find((v) => v.available) || product.variants[0];
  }

  /** Precio en pesos (products.json = moneda; products.js = centavos). */
  function productPrice(product) {
    const variant = pickVariant(product);
    const rawSource = variant && variant.price != null ? variant.price : product.price;
    if (rawSource == null || rawSource === '') return 0;

    const raw = Number(rawSource);
    if (!Number.isFinite(raw) || raw <= 0) return 0;

    // products.js usa centavos (number entero). products.json usa string tipo "3199.00".
    const fromProductJs = typeof rawSource === 'number' && Number.isInteger(rawSource);
    return fromProductJs ? raw / 100 : raw;
  }

  function productIsAvailable(product) {
    if (!product) return false;
    if (product.available === false) return false;
    return !!pickVariant(product) && productPrice(product) > 0;
  }

  function getOptionNames(product) {
    const options = product.options || [];
    return options.map((opt) => {
      if (typeof opt === 'string') return opt;
      return opt && opt.name ? opt.name : String(opt || '');
    });
  }

  function getColorOptionIndex(product) {
    const names = getOptionNames(product);
    return names.findIndex((name) => {
      const n = normalize(name);
      return n.includes('color') || n.includes('colour') || n.includes('tono') || n.includes('colour');
    });
  }

  function getProductColors(product) {
    const colors = new Set();
    const idx = getColorOptionIndex(product);
    if (idx < 0) return colors;

    (product.variants || []).forEach((variant) => {
      let value = '';
      if (variant.options && variant.options[idx] != null) {
        value = variant.options[idx];
      } else {
        value = variant['option' + (idx + 1)] || '';
      }
      const n = normalize(value);
      if (n) colors.add(n);
    });

    return colors;
  }

  function getProductTags(product) {
    const tags = new Set();
    const raw = product.tags;

    const list = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : [];

    list.forEach((tag) => {
      const n = normalize(tag);
      if (!n || IGNORED_TAGS.has(n)) return;
      tags.add(n);
    });

    return tags;
  }

  /** Todas las tags normalizadas (incluye género). */
  function getAllProductTags(product) {
    const tags = new Set();
    const raw = product.tags;

    const list = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : [];

    list.forEach((tag) => {
      const n = normalize(tag);
      if (n) tags.add(n);
    });

    return tags;
  }

  /** Devuelve 'mujer', 'hombre' o '' según las tags del producto. */
  function getProductGender(product) {
    const tags = getAllProductTags(product);
    if (tags.has('mujer')) return 'mujer';
    if (tags.has('hombre')) return 'hombre';
    return '';
  }

  function productMatchesCartGender(product, cartGender) {
    if (!cartGender) return true;
    return getProductGender(product) === cartGender;
  }

  function hasOnlyDefaultVariant(product) {
    if (!product.variants || product.variants.length <= 1) return true;
    const options = getOptionNames(product);
    return options.length === 1 && normalize(options[0]) === 'title';
  }

  function getSizeOptionIndex(product) {
    const options = getOptionNames(product);
    const idx = options.findIndex((name) => isSizeOptionName(name));
    return idx >= 0 ? idx : 0;
  }

  function isSizeOptionName(name) {
    const n = normalize(name);
    return (
      n.includes('size') ||
      n.includes('talla') ||
      n.includes('talle') ||
      n.includes('tamano')
    );
  }

  /** Normaliza tallas: "25.0" y "25" cuentan igual. */
  function normalizeSize(value) {
    return normalize(value).replace(/\.0+$/, '');
  }

  function getSizeLabel(variant, optionIndex) {
    if (variant.options && variant.options[optionIndex] != null) {
      return variant.options[optionIndex];
    }
    return variant['option' + (optionIndex + 1)] || variant.title || '';
  }

  /** Talla elegida en un item del carrito (Talla / Tamaño / Size). */
  function getCartItemSize(item) {
    if (!item) return '';

    if (item.options_with_values && item.options_with_values.length) {
      for (let i = 0; i < item.options_with_values.length; i++) {
        const opt = item.options_with_values[i];
        if (isSizeOptionName(opt.name)) return normalizeSize(opt.value);
      }
    }

    // Fallback: a veces viene como variant_options / options
    if (item.variant_options && item.variant_options.length) {
      // Sin nombre de opción; no asumir. Mejor vacío.
    }

    return '';
  }

  /** ¿Tiene alguna variante disponible en esa talla? */
  function productHasAvailableSize(product, size) {
    if (!size) return true;

    const optionIndex = getOptionNames(product).findIndex((name) => isSizeOptionName(name));
    if (optionIndex < 0) return false;

    return (product.variants || []).some((variant) => {
      if (variant.available === false) return false;
      return normalizeSize(getSizeLabel(variant, optionIndex)) === size;
    });
  }

  function buildSizeButtons(product) {
    if (hasOnlyDefaultVariant(product)) return '';

    const optionIndex = getSizeOptionIndex(product);
    const seen = new Set();
    const buttons = [];

    (product.variants || []).forEach((variant) => {
      const label = getSizeLabel(variant, optionIndex);
      if (!label || seen.has(label)) return;
      seen.add(label);

      const available = variant.available !== false;
      buttons.push(
        '<button type="button" class="msi-milestones__size' +
          (!available ? ' is-unavailable' : '') +
          '" data-msi-size-add="' +
          variant.id +
          '"' +
          (available ? '' : ' disabled') +
          ' aria-label="Talla ' +
          label +
          '">' +
          label +
          '</button>'
      );
    });

    if (!buttons.length) return '';

    return (
      '<div class="msi-milestones__sizes" data-msi-sizes>' +
      buttons.join('') +
      '<button type="button" class="msi-milestones__size-cancel" data-msi-size-cancel>Cancelar</button>' +
      '</div>'
    );
  }

  function sharesAny(setA, setB) {
    for (const value of setA) {
      if (setB.has(value)) return true;
    }
    return false;
  }

  async function buildCartProfile(cart) {
    const profile = {
      productIds: new Set(),
      types: new Set(),
      tags: new Set(),
      colors: new Set(),
      gender: '',
      size: ''
    };

    const items = (cart && cart.items) || [];

    // Talla del primer producto del carrito
    if (items.length) {
      profile.size = getCartItemSize(items[0]);
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.product_id) profile.productIds.add(item.product_id);

      if (item.product_type) profile.types.add(normalize(item.product_type));

      if (item.options_with_values) {
        item.options_with_values.forEach((opt) => {
          const name = normalize(opt.name);
          if (name.includes('color') || name.includes('colour') || name.includes('tono')) {
            const val = normalize(opt.value);
            if (val) profile.colors.add(val);
          }
        });
      }

      if (item.handle) {
        const product = await fetchProductByHandle(item.handle);
        if (!product) continue;

        if (product.product_type) profile.types.add(normalize(product.product_type));
        getProductTags(product).forEach((tag) => profile.tags.add(tag));
        getProductColors(product).forEach((color) => profile.colors.add(color));

        // Primer género detectado en el carrito (mujer / hombre)
        if (!profile.gender) {
          const gender = getProductGender(product);
          if (gender) profile.gender = gender;
        }
      }
    }

    return profile;
  }

  /**
   * Complementarios = mismo género + misma talla en stock + distinto tipo/tags/color.
   * Opcional: precio máximo. Devuelve hasta `limit` productos al azar.
   */
  function pickComplementaryProducts(products, cartProfile, excludeIds, limit, maxPrice) {
    const candidates = products.filter((p) => {
      if (!productIsAvailable(p)) return false;
      if (excludeIds.has(p.id)) return false;
      if (cartProfile.productIds.has(p.id)) return false;
      if (!productMatchesCartGender(p, cartProfile.gender)) return false;
      if (!productHasAvailableSize(p, cartProfile.size)) return false;
      if (maxPrice > 0 && productPrice(p) > maxPrice) return false;
      return true;
    });

    const opposite = candidates.filter((p) => {
      const type = normalize(p.product_type);
      if (type && cartProfile.types.size && cartProfile.types.has(type)) return false;

      const tags = getProductTags(p);
      if (tags.size && cartProfile.tags.size && sharesAny(tags, cartProfile.tags)) return false;

      const colors = getProductColors(p);
      if (colors.size && cartProfile.colors.size && sharesAny(colors, cartProfile.colors)) return false;

      return true;
    });

    const pool = (opposite.length ? opposite : candidates).slice();
    if (!pool.length) return [];

    // Shuffle Fisher–Yates
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = pool[i];
      pool[i] = pool[j];
      pool[j] = tmp;
    }

    return pool.slice(0, limit);
  }

  async function buildSuggestions(cart, config) {
    if (!config.collectionHandle) return [];

    const usedIds = new Set((cart.items || []).map((i) => i.product_id));
    const cartProfile = await buildCartProfile(cart);
    const products = await fetchCollectionProducts(config.collectionHandle);
    const picked = pickComplementaryProducts(
      products,
      cartProfile,
      usedIds,
      2,
      config.maxPrice
    );

    return picked.map((product) => ({
      product: product,
      price: productPrice(product)
    }));
  }

  function productImageUrl(product) {
    if (product.featured_image) {
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image.src) return product.featured_image.src;
    }
    if (product.images && product.images.length) {
      const img = product.images[0];
      return typeof img === 'string' ? img : img.src || '';
    }
    return '';
  }

  function renderBanner(el, subtotal, milestones) {
    const banner = el.querySelector('[data-msi-banner]');
    const title = el.querySelector('[data-msi-banner-title]');
    if (!banner || !title || !milestones.length) return;

    const unlocked = getHighestUnlocked(subtotal, milestones);
    const first = milestones[0];

    if (unlocked) {
      const phrase = formatRewardPhrase(unlocked.reward);
      title.innerHTML = 'Disfruta <strong>' + phrase + '</strong> en esta compra';
      banner.hidden = false;
      return;
    }

    // Aún no alcanza el primer milestone
    const remaining = Math.max(0, first.amount - subtotal);
    const phrase = formatRewardPhrase(first.reward);
    title.innerHTML =
      'Agrega un producto de <strong>' +
      formatCurrency(remaining) +
      '</strong> o más y disfruta de <strong>' +
      phrase +
      '</strong>';
    banner.hidden = false;
  }

  function getAnchorProductTitle(cart) {
    const items = cart && cart.items ? cart.items : [];
    if (!items.length) return '';
    return items[0].product_title || items[0].title || '';
  }

  function renderProductsHeader(el, cart) {
    const sub = el.querySelector('[data-msi-products-sub]');
    if (!sub) return;

    const title = getAnchorProductTitle(cart);
    sub.textContent = title
      ? 'Una selección pensada para acompañar ' + title
      : 'Una selección pensada para acompañar';
  }

  function renderProducts(el, suggestions, cart) {
    const wrap = el.querySelector('[data-msi-products]');
    const grid = el.querySelector('[data-msi-products-grid]');
    if (!wrap || !grid) return;

    if (!suggestions.length) {
      wrap.hidden = true;
      grid.innerHTML = '';
      return;
    }

    renderProductsHeader(el, cart);

    grid.innerHTML = suggestions
      .map(({ product, price }) => {
        const variant = pickVariant(product);
        const imgUrl = productImageUrl(product);
        const needsSize = !hasOnlyDefaultVariant(product);
        const safeTitle = String(product.title || '').replace(/"/g, '&quot;');

        return (
          '<article class="msi-milestones__product" data-msi-product-id="' +
          product.id +
          '">' +
          '<a class="msi-milestones__product-link" href="/products/' +
          product.handle +
          '">' +
          '<div class="msi-milestones__product-image">' +
          (imgUrl
            ? '<img src="' +
              imgUrl +
              '" alt="' +
              safeTitle +
              '" loading="lazy" width="240" height="240">'
            : '') +
          '</div>' +
          '<p class="msi-milestones__product-title">' +
          product.title +
          '</p>' +
          '<p class="msi-milestones__product-price">' +
          formatCurrency(price) +
          '</p>' +
          '</a>' +
          '<button type="button" class="msi-milestones__add btn btn--secondary btn--small"' +
          ' data-msi-add="' +
          (variant ? variant.id : '') +
          '"' +
          ' data-msi-needs-size="' +
          (needsSize ? 'true' : 'false') +
          '"' +
          ' data-msi-product-id="' +
          product.id +
          '">Agregar</button>' +
          buildSizeButtons(product) +
          '</article>'
        );
      })
      .join('');

    wrap.hidden = false;
  }

  async function addToCart(variantId, button) {
    if (!variantId) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = '…';

    try {
      const res = await fetch((window.theme?.routes?.cart?.add || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });

      if (!res.ok) throw new Error('add failed');

      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
    } catch (err) {
      console.error('[msi-milestones] Error al agregar', err);
      button.textContent = 'Error';
      setTimeout(function () {
        button.textContent = original;
        button.disabled = false;
      }, 1500);
      return;
    }

    button.textContent = original;
    button.disabled = false;
  }

  function closeSizeSelectors(exceptCard) {
    document.querySelectorAll('.msi-milestones__product.is-selecting-size').forEach((card) => {
      if (exceptCard && card === exceptCard) return;
      card.classList.remove('is-selecting-size');
    });
  }

  async function updateFromCart(cart) {
    const els = getRoots();
    if (!els.length || !cart) return;

    const itemCount = cart.item_count || (cart.items && cart.items.length) || 0;
    const subtotal = (cart.total_price || 0) / 100;

    for (const el of els) {
      const config = getConfig();
      if (!config.milestones.length) continue;

      if (!itemCount) {
        el.hidden = true;
        continue;
      }

      el.hidden = false;

      renderBanner(el, subtotal, config.milestones);

      const suggestions = await buildSuggestions(cart, config);
      renderProducts(el, suggestions, cart);
    }
  }

  function bindClicks() {
    document.addEventListener('click', function (e) {
      const toggle = e.target.closest('[data-msi-products-toggle]');
      if (toggle) {
        e.preventDefault();
        const wrap = toggle.closest('[data-msi-products]');
        if (!wrap) return;

        const collapsed = wrap.classList.toggle('is-collapsed');
        toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        toggle.setAttribute(
          'aria-label',
          collapsed ? 'Mostrar productos sugeridos' : 'Ocultar productos sugeridos'
        );
        return;
      }

      const cancel = e.target.closest('[data-msi-size-cancel]');
      if (cancel) {
        e.preventDefault();
        const card = cancel.closest('.msi-milestones__product');
        if (card) card.classList.remove('is-selecting-size');
        return;
      }

      const sizeBtn = e.target.closest('[data-msi-size-add]');
      if (sizeBtn) {
        e.preventDefault();
        if (sizeBtn.disabled) return;
        addToCart(sizeBtn.dataset.msiSizeAdd, sizeBtn);
        return;
      }

      const addBtn = e.target.closest('[data-msi-add]');
      if (!addBtn) return;
      e.preventDefault();

      const needsSize = addBtn.dataset.msiNeedsSize === 'true';
      const card = addBtn.closest('.msi-milestones__product');

      if (needsSize && card) {
        closeSizeSelectors(card);
        card.classList.add('is-selecting-size');
        return;
      }

      addToCart(addBtn.dataset.msiAdd, addBtn);
    });
  }

  function init() {
    if (!getRoots().length) return;

    bindClicks();

    document.addEventListener('cart:updated', function (evt) {
      updateFromCart(evt.detail && evt.detail.cart);
    });

    document.addEventListener('quick-cart:open', function (evt) {
      updateFromCart(evt.detail && evt.detail.cart);
    });

    fetch((window.theme?.routes?.cart?.base || '/cart') + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(updateFromCart)
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
